import { UI_AVATAR_API_END_POINT } from "../constant";
import { log, LogCategory } from "./log";

const logE = (event: string, ...args: any[]) => log(LogCategory.Cache, "##### " + event, ...args);

const MAX_CACHE_SIZE_MB = 5; // Maximum total cache size in MB
const MAX_IMAGE_SIZE_MB = 1; // Maximum individual image size in MB
const BYTES_PER_MB = 1024 * 1024;

const imageCache = new Map<string, string>();
let currentCacheSize = 0;

type IconMap = {
  [key: string]: string;
};

const icons = import.meta.glob("../assets/icons/app/*.(svg|png|ico)", {
  eager: true,
  as: "url",
}) as Record<string, string>;

export const localIconMap: IconMap = Object.fromEntries(
  Object.entries(icons).map(([path, url]) => [path.split("/").pop() || "", url]),
);

export const loadLocalIcon = (url: string): string | undefined => {
  try {
    const filename = url.split("?")[0].split("/").pop();

    if (!filename) return;

    return localIconMap[filename];
  } catch (error) {
    return;
  }
};

export async function cachedImage(url: string): Promise<string> {
  try {
    let cacheKey = url.split("?")[0];
    if (url.startsWith(UI_AVATAR_API_END_POINT)) {
      cacheKey = url.split("?")[1];
    }

    const localIcon = loadLocalIcon(cacheKey);
    if (localIcon) {
      logE(`Found local cached icon: ${cacheKey}`);
      return localIcon;
    }

    if (imageCache.has(cacheKey)) {
      logE(`Found cached icon: ${cacheKey}`);
      return imageCache.get(cacheKey) || url;
    }

    if (currentCacheSize >= MAX_CACHE_SIZE_MB * BYTES_PER_MB) {
      logE(`Cache full, skipping: ${cacheKey}`);
      return url;
    }

    const response = await fetch(url);
    const blob = await response.blob();

    if (blob.size > MAX_IMAGE_SIZE_MB * BYTES_PER_MB) {
      logE(`Image too large (${(blob.size / BYTES_PER_MB).toFixed(2)}MB), skipping: ${cacheKey}`);
      return url;
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64data = reader.result as string;

        imageCache.set(cacheKey, base64data);
        currentCacheSize += blob.size;

        logE(
          `Cached image (${(blob.size / BYTES_PER_MB).toFixed(2)}MB), Total cache: ${(currentCacheSize / BYTES_PER_MB).toFixed(2)}MB. URL: ${cacheKey}`,
        );
        resolve(base64data);
      };

      reader.readAsDataURL(blob);
    });
  } catch (error) {
    logE(`Error caching image: ${url}`, error);
    return url;
  }
}
